import { Calendar as CalendarUI, CalendarEventDialog } from '@tunasong/calendar/ui'
import { toAttendee } from '@tunasong/calendar'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Add, Calendar as CalendarIcon } from '@tunasong/icons'
import { dayjs, entitySort, matchNone, shortUuid } from '@tunasong/models'
import type { LayoutComponent } from '@tunasong/plugin-lib'
import { getAcceptedMimeTypes, getDefaultYDocString, usePlugins } from '@tunasong/plugin-lib'
import type { BandSpace, Calendar, CalendarEvent, Entity, Persisted } from '@tunasong/schemas'
import { isBandSpace, isCalendar, isCalendarEvent, isComment, isNote, isPersisted } from '@tunasong/schemas'
import { RecentActivity } from '@tunasong/tunasong-ui'
import {
  AccessAvatarList,
  Button,
  Divider,
  DndList,
  DropZone,
  Grid,
  HBox,
  mergeElementOrder,
  SortFilterBar,
  useNavigateToEntity,
  useSortFilterOptions,
} from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useCallback, useState } from 'react'
import invariant from 'tiny-invariant'
import styles from './default.module.css'

export interface BandSpaceProps extends LayoutComponent<BandSpace> {
  className?: string
}

// Initial child filter to exclude calendars and comments
const childFilter = matchNone(isCalendar, isComment, isNote)

const DefaultView: FC<BandSpaceProps> = props => {
  const { entity } = props

  const plugins = usePlugins('all')
  const mimeTypes = getAcceptedMimeTypes(plugins)

  const { members } = graphHooks.useMembers(entity)
  const organizer = members.find(member => member.userId === entity.userId)

  const { sortOptions, setSortOptions, filter, setFilter } = useSortFilterOptions(entity.id)

  const {
    entities: childEntities,
    isLoading,
    isFetching,
  } = graphHooks.useEntityChildren({
    parentId: entity.id,
    filter: childFilter,
    defaultSort: true,
    includeSys: false,
  })

  const { edgeEntities: calendars } = graphHooks.useEdgeEntities<BandSpace, Calendar>({
    sourceId: entity.id,
    relation: 'calendar',
  })
  const calendar = calendars[0]?.target
  const { entities: events } = graphHooks.useEntityChildren<CalendarEvent>({
    parentId: calendar?.id,
    filter: isCalendarEvent,
    includeSys: false,
  })

  const { createEntity } = graphHooks.useEntityCreate()

  const [eventOpen, setEventOpen] = useState(false)
  const handleNewEvent = useCallback(
    async (event: CalendarEvent) => {
      await createEntity({
        parent: calendar,
        entity: {
          // A client id plus `optimistic: true`: the event appears in the calendar at once
          id: shortUuid(),
          ...event,
        },
        // List-only: nothing opens the event, so the temporary entity is safe here (#1958)
        optimistic: true,
      })
      setEventOpen(false)
    },
    [calendar, createEntity]
  )

  const navigateToEntity = useNavigateToEntity()

  const handleSelectEvent = useCallback(
    (event: CalendarEvent) => {
      if (isPersisted(event)) {
        navigateToEntity(event)
      }
    },
    [navigateToEntity]
  )

  // const { searchResults = [], isLoading: isLoadingRecent } = useRecent({
  //   exclude: [entity],
  //   maxCount: 9,
  //   parentId: entity.id,
  // })

  const [event, setEvent] = useState<CalendarEvent>()

  const handleDateClick = (date: Date) => {
    const d = dayjs(date).set('hour', 17).set('minutes', 0)
    const attendees = members.map(m => toAttendee(m))
    invariant(organizer, 'Organizer not found')
    setEvent({
      type: 'calendarevent',
      organizer: toAttendee(organizer),
      start: d.toISOString(),
      end: d.add(1, 'hour').toISOString(),
      attendees,
      yDoc: getDefaultYDocString(),
    })
    setEventOpen(true)
  }

  if (!(isBandSpace(entity) && isPersisted(entity))) {
    return null
  }

  const filteredChildEntities = entitySort({ entities: childEntities, sortOptions, typeFilter: filter })

  /**
   * A manual re-order in the Content list; same handling as the folder list. GH #2152. Items hidden by the type
   * filter keep their places.
   */
  const handleReorder = (entities: Persisted<Entity>[]) => {
    const fullOrder = entitySort({ entities: childEntities, sortOptions }).map(e => e.id)
    const elementOrder = mergeElementOrder(
      fullOrder,
      entities.map(e => e.id)
    )
    setSortOptions({ ...sortOptions, type: 'order', elementOrder })
  }

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Divider>Members</Divider>
        <AccessAvatarList entity={entity} size={'tiny'} />

        <Divider className={styles.activityDivider}>Recent Activity</Divider>
        <RecentActivity parentId={entity.id} type="grid" rows={3} size="small" />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        {calendar && organizer ? (
          <>
            <Divider>Calendar</Divider>
            <CalendarUI
              events={events}
              initialView="listMonth"
              onSelectEvent={handleSelectEvent}
              onDateClick={handleDateClick}
            />
            <HBox>
              <Button startIcon={<Add />} fullWidth disabled={!calendar} onClick={() => setEventOpen(true)}>
                Create event
              </Button>
              <Button endIcon={<CalendarIcon />} fullWidth onClick={() => navigateToEntity(calendar)}></Button>
            </HBox>
            {eventOpen ? (
              // Recreate the dialog each time to avoid stale state
              <CalendarEventDialog
                open={true}
                organizer={organizer}
                defaultEvent={event}
                defaultAttendees={members}
                title={'Calendar event'}
                onSave={handleNewEvent}
                onClose={() => setEventOpen(false)}
              />
            ) : null}
          </>
        ) : null}
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Divider>Content</Divider>
        {childEntities.length > 0 || filter.length > 0 ? (
          <SortFilterBar
            className={styles.sortFilterBar}
            entities={childEntities}
            filter={filter}
            onFilter={setFilter}
            sortOptions={sortOptions}
            onSort={setSortOptions}
          />
        ) : null}

        <DropZone parentId={entity.id} accept={mimeTypes} uploadOnClick showClickZone>
          {/* The folder list's drag-and-drop list: drag onto a folder to move into it, elsewhere to re-order. GH #2152 */}
          <DndList
            isLoading={isLoading}
            isFetching={isFetching}
            entities={filteredChildEntities}
            onOpen={navigateToEntity}
            onChange={handleReorder}
            optionsKey={entity.id}
            size="large"
            sort={true}
          />
        </DropZone>
      </Grid>
    </Grid>
  )
}

export default DefaultView
